// packages/web/src/lib/coverFrameCapture.ts — B11: P2's captureAt/rescrub
// pair, extracted out of RecordForm.tsx's FileField so a SECOND caller
// (Materials -> Reels, screens/MaterialsReelsAssist.tsx) never copies it.
// RecordForm's own FileField now calls this hook too — one extraction path,
// two callers, exactly the shape F0's "a vertical's noun never gets
// hardcoded into shared code" already asks for (this file names nothing
// about `aa_media`).
//
// What moved here: the extraction/upload/preview/scrub STATE and the two
// acts on it (`capture`, `rescrub`). What did NOT move: what a caller does
// with the resulting `cover_frame_ref` blob ref — RecordForm writes it to a
// sibling FORM FIELD that saves later; MaterialsReelsAssist writes it
// straight to an `aa_media` record that already exists. That is why
// `onCaptured` is a callback the caller owns, not a URL this hook posts to.
//
// ⚠ NEVER SILENT — same rule as coverFrame.ts itself. A failed extraction
// sets `extractError` and stops there; it never throws past this hook and
// never touches whatever upload already succeeded (the video/reel itself is
// in either way — see both callers' own comments).
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { CoverFrameExtractionError, coverFrameFilename, extractCoverFrame } from './coverFrame';
import { uploadRecordBlob } from './api';

export interface CoverFrameCaptureState {
  extracting: boolean;
  extractError: string | null;
  /** An object URL for the chosen still — caller renders it directly, never
   * revokes it itself (the hook owns the URL's lifetime, including on
   * unmount). */
  previewUrl: string | null;
  seekSeconds: number;
  /** Null until a first capture succeeds — the scrub control (both callers'
   * own JSX) is offered only once a real duration is known. */
  durationSeconds: number | null;
}

export interface UseCoverFrameCapture {
  state: CoverFrameCaptureState;
  /** Extract + upload a still for `file`, seeking to `atSeconds` (the
   * extractor's own default seek when omitted — never frame 0). Fires
   * `onCaptured` with the uploaded blob ref on success; sets
   * `state.extractError` and touches nothing else on failure. */
  capture: (file: File, atSeconds?: number) => Promise<void>;
  /** Re-run `capture` against the LAST file capture() was given, at a new
   * moment — a no-op before any capture has run. */
  rescrub: (seconds: number) => Promise<void>;
  /** Move the displayed scrub value WITHOUT re-extracting — a slider's own
   * `onInput` (dragging) vs. `rescrub`'s `onChange` (releasing). Cheap, and
   * never touches the network; the caller commits with `rescrub` once the
   * artist has actually settled on a moment. */
  setSeekSeconds: (seconds: number) => void;
}

/**
 * ⚠ `onCaptured` IS READ THROUGH A REF, ALWAYS THE LATEST ONE PASSED IN —
 * not the one closed over when `capture()` was first called. A caller whose
 * routing target is only known AFTER this hook is already mounted (B11:
 * `mediaId` does not exist until the video's own upload finishes, and
 * extraction starts only once it has) can still route a later rescrub
 * correctly without re-mounting the hook. Same "recreated every render,
 * refed for the async path" shape App.tsx's own push-to-talk context uses.
 */
export function useCoverFrameCapture(tenantId: string, onCaptured: (ref: string) => void): UseCoverFrameCapture {
  const [state, setState] = useState<CoverFrameCaptureState>({
    extracting: false,
    extractError: null,
    previewUrl: null,
    seekSeconds: 0,
    durationSeconds: null,
  });

  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;
  const videoMetaRef = useRef<{ file: File; duration: number } | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  // The last object URL is revoked on unmount too — the same discipline
  // MediaEl.tsx's useMediaObjectUrl already follows for the same leak.
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const capture = useCallback(
    async (file: File, atSeconds?: number): Promise<void> => {
      setState((s) => ({ ...s, extracting: true, extractError: null }));
      try {
        const captured = await extractCoverFrame(file, atSeconds);
        const stillFile = new File([captured.blob], coverFrameFilename(file.name), { type: 'image/jpeg' });
        const { ref } = await uploadRecordBlob(tenantId, stillFile);
        onCapturedRef.current(ref);
        const url = URL.createObjectURL(captured.blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        videoMetaRef.current = { file, duration: captured.durationSeconds };
        setState((s) => ({
          ...s,
          extracting: false,
          extractError: null,
          previewUrl: url,
          seekSeconds: captured.atSeconds,
          durationSeconds: captured.durationSeconds,
        }));
      } catch (exErr) {
        // ⚠ LOUD, NAMED, NEVER SILENT (P2's own rule, and F0's house-wide
        // one). Whatever upload already succeeded stands — this failure is
        // ONLY about the still, and both callers' own fallback is a plain
        // image upload into the same field.
        setState((s) => ({
          ...s,
          extracting: false,
          extractError:
            exErr instanceof CoverFrameExtractionError
              ? exErr.message
              : exErr instanceof Error
                ? exErr.message
                : 'Could not capture a cover frame from this video.',
        }));
      }
    },
    [tenantId]
  );

  const rescrub = useCallback(
    async (seconds: number): Promise<void> => {
      const meta = videoMetaRef.current;
      if (!meta) return;
      setState((s) => ({ ...s, seekSeconds: seconds }));
      await capture(meta.file, seconds);
    },
    [capture]
  );

  const setSeekSeconds = useCallback((seconds: number): void => {
    setState((s) => ({ ...s, seekSeconds: seconds }));
  }, []);

  return { state, capture, rescrub, setSeekSeconds };
}
